/**
 * 북마크 postList 문서의 `source` 로 **무엇을 담았는지** 가린다.
 *
 * 담기는 것은 문서 하나에 `postId` 하나뿐이라, 본문을 어느 원장에서 꺼낼지는 `source` 가 정한다.
 * ```
 *   'meta'              광고 소재   postId = adArchiveId      → ads.detail
 *   판매몰 코드          판매몰 상품  postId = 상품 id          → rankings.detail
 *   그 밖(채널 이름 등)   소셜 글     postId = 글 id            → feed.byIds
 * ```
 * 🔑 상품만 `source` 가 **판매몰 이름**이다 — 원장을 가르는 값이자 어느 몰인지까지 함께 쥔다
 *   (같은 상품 id 가 몰마다 따로 있어 몰을 잃으면 다시 찾을 수 없다).
 * ⚠️ 판매몰 코드와 소셜 채널 이름은 **겹치지 않는다** — 겹치는 값이 생기면 글이 상품으로 읽힌다.
 * ⚠️ 가리는 차례가 있다 — 광고 · 상품을 먼저 걸러 내고 **남은 것이 글**이다.
 */

import { PRODUCT_SOURCE_LABELS } from 'src/constants/product-sources';

export const BOOKMARK_AD_SOURCE = 'meta';

/** 상품으로 읽을 `source` 값 — 판매몰 코드 그대로다 */
export const BOOKMARK_PRODUCT_SOURCES = new Set(Object.keys(PRODUCT_SOURCE_LABELS));

/** @returns 광고 소재를 담은 postList 문서인가 */
export function isAdBookmarkDoc(doc: { source?: unknown } | null | undefined): boolean {
  return readSource(doc) === BOOKMARK_AD_SOURCE;
}

/** @returns 판매몰 상품을 담은 postList 문서인가 */
export function isProductBookmarkDoc(doc: { source?: unknown } | null | undefined): boolean {
  return BOOKMARK_PRODUCT_SOURCES.has(readSource(doc));
}

/** @returns 소셜 글을 담은 postList 문서인가 — 광고도 상품도 아니면 글이다 */
export function isPostBookmarkDoc(doc: { source?: unknown } | null | undefined): boolean {
  return !isAdBookmarkDoc(doc) && !isProductBookmarkDoc(doc);
}

/** @returns 담은 것의 종류 — 광고 `ad` · 판매몰 상품 `product` · 그 밖은 글 `post`(활동 로그 `item_type`) */
export function bookmarkKindOf(doc: { source?: unknown } | null | undefined): 'ad' | 'product' | 'post' {
  if (isAdBookmarkDoc(doc)) return 'ad';
  return isProductBookmarkDoc(doc) ? 'product' : 'post';
}

// ---- 이하 비공개 ----------------------------------------------------------

function readSource(doc: { source?: unknown } | null | undefined): string {
  return String(doc?.source ?? '').trim().toLowerCase();
}
